import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Car, MessageSquare, Calendar, ChevronRight, Users as UsersIcon,
  Newspaper, Zap, ArrowRight, Star, Shield, Compass, FileText,
  Award, Trophy, Activity, UserPlus, Flag, MapPin
} from 'lucide-react';
import AdSlot from '../components/AdSlot';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ─────────── Illustrations ─────────── */
function MicroCar({ color = 'emerald', className = '' }) {
  const palette = color === 'cyan' ? {
    body: '#2563eb', body2: '#1d4ed8', shadow: '#1e40af', window: '#dbeafe', detail: '#f8fafc'
  } : {
    body: '#f8fafc', body2: '#e2e8f0', shadow: '#94a3b8', window: '#334155', detail: '#f8fafc'
  };
  return (
    <svg viewBox="0 0 220 150" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* ground shadow */}
      <ellipse cx="110" cy="138" rx="90" ry="6" fill="#0f172a" opacity="0.12" />
      {/* body */}
      <path d="M30 110 Q30 70 70 55 L90 40 Q110 32 130 40 L150 55 Q190 70 190 110 L190 120 Q190 125 185 125 L35 125 Q30 125 30 120 Z"
        fill={palette.body} stroke={palette.shadow} strokeWidth="1.5" />
      {/* roof highlight */}
      <path d="M70 55 L90 40 Q110 32 130 40 L150 55 Z" fill={palette.body2} opacity="0.6" />
      {/* windshield */}
      <path d="M78 60 L94 46 Q110 40 126 46 L142 60 L128 72 Q110 68 92 72 Z" fill={palette.window} opacity="0.85" />
      {/* window divider */}
      <line x1="110" y1="42" x2="110" y2="72" stroke={palette.shadow} strokeWidth="1" opacity="0.3" />
      {/* side panel */}
      <rect x="40" y="100" width="140" height="4" rx="2" fill={palette.shadow} opacity="0.25" />
      {/* headlights */}
      <circle cx="42" cy="95" r="5" fill="#fde68a" stroke={palette.shadow} strokeWidth="0.5" />
      <circle cx="178" cy="95" r="5" fill="#fde68a" stroke={palette.shadow} strokeWidth="0.5" />
      {/* bumper */}
      <rect x="35" y="115" width="150" height="6" rx="3" fill={palette.body2} />
      {/* wheels */}
      <circle cx="60" cy="125" r="14" fill="#0f172a" />
      <circle cx="60" cy="125" r="7" fill="#475569" />
      <circle cx="60" cy="125" r="3" fill="#cbd5e1" />
      <circle cx="160" cy="125" r="14" fill="#0f172a" />
      <circle cx="160" cy="125" r="7" fill="#475569" />
      <circle cx="160" cy="125" r="3" fill="#cbd5e1" />
      {/* plate */}
      <rect x="95" y="112" width="30" height="7" rx="1" fill="#fef3c7" stroke={palette.shadow} strokeWidth="0.5" />
    </svg>
  );
}

const CATEGORY_COLORS = {
  genel: 'bg-slate-100 text-slate-700',
  l6e: 'bg-emerald-50 text-emerald-700',
  l7e: 'bg-cyan-50 text-cyan-700',
  'arac-incelemeleri': 'bg-violet-50 text-violet-700',
  'teknik-bakim': 'bg-orange-50 text-orange-700',
  'sarj-batarya': 'bg-yellow-50 text-yellow-700',
  mevzuat: 'bg-blue-50 text-blue-700',
  'ikinci-el': 'bg-rose-50 text-rose-700',
  'etkinlikler-forum': 'bg-purple-50 text-purple-700',
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'az önce';
  if (diff < 3600) return `${Math.floor(diff / 60)} dakika önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} gün önce`;
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function monthShort(iso) {
  if (!iso) return { d: '--', m: '---' };
  const dt = new Date(iso);
  return {
    d: String(dt.getDate()).padStart(2, '0'),
    m: dt.toLocaleDateString('tr-TR', { month: 'short' }),
  };
}

function initials(name) {
  if (!name) return 'U';
  return name.split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
}

/* ─────────── Hero ─────────── */
function Hero({ user, navigate }) {
  return (
    <section className="relative overflow-hidden bg-[#061a12] text-white" data-testid="home-hero">
      {/* aurora glow + grid */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(60% 70% at 85% 20%, rgba(16,185,129,0.35) 0%, transparent 60%), radial-gradient(40% 50% at 10% 90%, rgba(14,165,233,0.25) 0%, transparent 60%)' }} />
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '48px 48px', maskImage: 'linear-gradient(to bottom, black, transparent)' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 md:py-20">
        <div className="grid md:grid-cols-12 gap-10 md:gap-6 items-center">
          {/* LEFT */}
          <div className="md:col-span-6 lg:col-span-5 hero-reveal">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-300 mb-5">
              <Zap className="w-3.5 h-3.5" /> Türkiye'nin L6e · L7e topluluğu
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Küçük araç,<br />
              <span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-sky-300 bg-clip-text text-transparent">büyük topluluk.</span>
            </h1>
            <p className="text-base text-emerald-50/70 mb-8 leading-relaxed max-w-md">
              Hafif dört tekerlekli elektrikli araç sahiplerinin buluşma noktası. Forumda paylaş, modelleri karşılaştır, etkinliklere katıl.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {!user ? (
                <>
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold rounded-full px-6 shadow-lg shadow-emerald-500/30 transition-transform hover:-translate-y-0.5" onClick={() => navigate('/kayit')} data-testid="hero-register-btn">
                    <UserPlus className="mr-2 w-4 h-4" /> Hemen Üye Ol
                  </Button>
                  <Button size="lg" variant="outline" className="rounded-full px-6 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white backdrop-blur" onClick={() => navigate('/forum')} data-testid="hero-forum-btn">
                    <Compass className="mr-2 w-4 h-4" /> Forum'u Keşfet
                  </Button>
                </>
              ) : (
                <>
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold rounded-full px-6 shadow-lg shadow-emerald-500/30 transition-transform hover:-translate-y-0.5" onClick={() => navigate('/forum')} data-testid="hero-forum-btn">
                    Foruma Git <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <Button size="lg" variant="outline" className="rounded-full px-6 border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white backdrop-blur" onClick={() => navigate('/garaj')} data-testid="hero-garage-btn">
                    <Car className="mr-2 w-4 h-4" /> Garajım
                  </Button>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs sm:text-sm text-emerald-50/60">
              <span className="flex items-center gap-1.5"><Car className="w-4 h-4 text-emerald-400" /> Araç Kataloğu</span>
              <span className="flex items-center gap-1.5"><MessageSquare className="w-4 h-4 text-emerald-400" /> Aktif Forum</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-emerald-400" /> Etkinlikler</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-400" /> Güvenli Topluluk</span>
            </div>
          </div>

          {/* RIGHT - photo */}
          <div className="md:col-span-6 lg:col-span-7 relative hero-reveal" style={{ animationDelay: '120ms' }}>
            <div className="relative rounded-[1.75rem] overflow-hidden border border-white/10 shadow-2xl shadow-emerald-900/50 group">
              <img src="/img/hero-ami.webp" alt="Aurora ışıkları altında L6e elektrikli mikro araç"
                className="w-full h-[260px] sm:h-[340px] lg:h-[420px] object-cover object-[60%_center] transition-transform duration-700 group-hover:scale-[1.03]" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#061a12]/70 via-transparent to-transparent" />
              {/* floating chips */}
              <div className="absolute left-4 bottom-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-emerald-950 text-xs font-bold px-3 py-1.5 shadow-lg">
                  L6e <span className="font-medium text-emerald-900/80">· 45 km/s · 425 kg</span>
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/90 text-slate-900 text-xs font-bold px-3 py-1.5 shadow-lg backdrop-blur">
                  L7e <span className="font-medium text-slate-600">· 90 km/s · 450 kg</span>
                </span>
              </div>
              <div className="absolute right-4 top-4 rounded-xl bg-black/40 backdrop-blur-md border border-white/10 px-3 py-2 text-[11px] text-emerald-50/80 hidden sm:block">
                <div className="font-semibold text-white">%100 Elektrikli</div>
                Şehir içi mikromobilite
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Category Cards ─────────── */
function CategoryCards() {
  const cards = [
    { to: '/forum/kategori/l6e', id: 'home-l6e-card', code: 'L6e', img: '/img/card-l6e.webp', pos: 'object-[55%_center]',
      sub: 'Hafif Dört Tekerlekli', spec: 'Maks. 45 km/s · 425 kg · B1 ehliyet', tone: 'from-emerald-950/95 via-emerald-900/70', chip: 'bg-emerald-400 text-emerald-950', cta: 'L6e Araçları İncele' },
    { to: '/forum/kategori/l7e', id: 'home-l7e-card', code: 'L7e', img: '/img/card-l7e.webp', pos: 'object-[45%_center]',
      sub: 'Ağır Dört Tekerlekli', spec: 'Maks. 90 km/s · 450 kg · B ehliyet', tone: 'from-slate-950/95 via-indigo-950/70', chip: 'bg-sky-400 text-sky-950', cta: 'L7e Araçları İncele' },
  ];
  return (
    <>
      {cards.map(c => (
        <Link key={c.code} to={c.to} data-testid={c.id}
          className="relative overflow-hidden rounded-2xl text-white min-h-[220px] group shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300">
          <img src={c.img} alt={`${c.code} elektrikli araç`} className={`absolute inset-0 w-full h-full object-cover ${c.pos} transition-transform duration-700 group-hover:scale-105`} />
          <div className={`absolute inset-0 bg-gradient-to-r ${c.tone} to-transparent`} />
          <div className="relative z-10 p-6 flex flex-col h-full min-h-[220px]">
            <span className={`self-start text-2xl font-extrabold px-3 py-0.5 rounded-lg ${c.chip}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{c.code}</span>
            <p className="mt-3 font-semibold text-white/95">{c.sub}</p>
            <p className="text-xs text-white/70 mt-0.5">{c.spec}</p>
            <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white group-hover:gap-2.5 transition-all">
              {c.cta} <ChevronRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      ))}
    </>
  );
}

/* ─────────── Quick Access ─────────── */
function QuickAccess() {
  const items = [
    { icon: MessageSquare, label: 'Forum', to: '/forum', color: 'bg-emerald-50 text-emerald-600' },
    { icon: Car, label: 'Araç Kataloğu', to: '/araclar', color: 'bg-sky-50 text-sky-600' },
    { icon: Calendar, label: 'Etkinlikler', to: '/etkinlikler', color: 'bg-violet-50 text-violet-600' },
    { icon: Newspaper, label: 'Haberler', to: '/haberler', color: 'bg-amber-50 text-amber-600' },
    { icon: UsersIcon, label: 'Kullanıcı Profilleri', to: '/kullanicilar', color: 'bg-rose-50 text-rose-600' },
  ];
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <h3 className="text-lg font-bold text-slate-800 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Hızlı Erişim</h3>
      <div className="space-y-1">
        {items.map(it => {
          const I = it.icon;
          return (
            <Link key={it.to} to={it.to} data-testid={`quick-${it.label}`}
              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors group">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${it.color}`}>
                <I className="w-5 h-5" />
              </div>
              <span className="flex-1 text-sm font-medium text-slate-700 group-hover:text-slate-900">{it.label}</span>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────── Section header ─────────── */
function SectionHeader({ icon: Icon, title, to }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {Icon && <Icon className="w-5 h-5 text-emerald-600" />}
        {title}
      </h2>
      {to && (
        <Link to={to} className="text-emerald-600 hover:text-emerald-700 text-xs font-semibold flex items-center gap-1">
          Tümünü Gör <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

/* ─────────── Main Page ─────────── */
export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recentTopics, setRecentTopics] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [badges, setBadges] = useState([]);
  const [showcase, setShowcase] = useState([]);

  useEffect(() => {
    axios.get(`${API}/forum/topics?sort=recent&limit=5`).then(r => setRecentTopics(r.data.topics || [])).catch(() => {});
    axios.get(`${API}/events`).then(r => setRecentEvents((r.data.events || []).slice(0, 3))).catch(() => {});
    axios.get(`${API}/news`).then(r => setNews(r.data.news || [])).catch(() => {});
    axios.get(`${API}/vehicles`).then(r => setVehicles((r.data.vehicles || []).slice(0, 5))).catch(() => {});
    axios.get(`${API}/users?limit=5&sort=new`).then(r => setUsers(r.data.users || [])).catch(() => {});
    axios.get(`${API}/badges`).then(r => setBadges((r.data.badges || []).slice(0, 7))).catch(() => {});
    axios.get(`${API}/garage/showcase?limit=8`).then(r => setShowcase(r.data.vehicles || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="MiniOto · L6e ve L7e Araçlar için Mikromobilite Topluluğu"
        description="L6e ve L7e kategorisindeki hafif dört tekerlekli araç sahiplerinin ve meraklılarının buluşma noktası. Forumda paylaş, araçlarını keşfet, etkinliklere katıl."
        noSuffix
      />
      <Hero user={user} navigate={navigate} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <AdSlot placement="home_top" className="mb-6" />

        {/* ROW 1: Category cards + Quick Access */}
        <div className="grid lg:grid-cols-3 gap-5 mb-8">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
            <CategoryCards />
          </div>
          <QuickAccess />
        </div>

        {/* ROW 2: Forum, Vehicles, Promo+News */}
        <AdSlot placement="home_middle" className="mb-6" />
        <div className="grid lg:grid-cols-3 gap-5 mb-8">
          {/* Popular topics */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <SectionHeader icon={MessageSquare} title="Son Forum Konuları" to="/forum" />
            {recentTopics.length === 0 ? (
              <EmptyState icon={MessageSquare} text="Henüz konu yok" cta={{ label: 'İlk konuyu aç', to: '/forum' }} />
            ) : (
              <div className="space-y-2">
                {recentTopics.map(t => (
                  <Link key={t._id} to={`/forum/konu/${t.slug || t._id}`} data-testid={`home-topic-${t._id}`}
                    className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                    <Avatar className="w-9 h-9 shrink-0">
                      <AvatarImage src={t.author_avatar ? (t.author_avatar.startsWith('http') ? t.author_avatar : `${API}/files/${t.author_avatar}`) : ''} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-bold">{initials(t.author_display_name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 line-clamp-1">{t.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[t.category_slug] || 'bg-slate-100 text-slate-600'}`}>
                          {t.category_name}
                        </span>
                        <span className="text-xs text-slate-400">{t.author_display_name || t.author_username}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-slate-500 font-semibold">{t.reply_count || 0} yanıt</div>
                      <div className="text-[10px] text-slate-400">{timeAgo(t.created_at)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Popular vehicles */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <SectionHeader icon={Car} title="Popüler Araç Modelleri" to="/araclar" />
            {vehicles.length === 0 ? (
              <EmptyState icon={Car} text="Henüz araç yok" />
            ) : (
              <div className="space-y-2">
                {vehicles.map(v => (
                  <Link key={v._id} to="/araclar" data-testid={`home-vehicle-${v._id}`}
                    className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                    <div className="w-12 h-12 shrink-0 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center">
                      {v.image_url ? (
                        <img
                          src={v.image_url.startsWith('http') ? v.image_url : `${API}/files/${v.image_url}`}
                          alt={v.model}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <MicroCar color={v.category === 'L7e' ? 'cyan' : 'emerald'} className="w-10 h-10" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800 truncate">{v.brand} {v.model}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${v.category === 'L7e' ? 'bg-sky-50 text-sky-700' : 'bg-emerald-50 text-emerald-700'}`}>
                          {v.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Star key={n} className={`w-3 h-3 ${n <= Math.round(v.rating_avg || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                        ))}
                        {v.rating_count > 0 ? (
                          <span className="text-[10px] text-slate-500 ml-1 font-medium">{v.rating_avg} <span className="text-slate-400 font-normal">({v.rating_count})</span></span>
                        ) : (
                          <span className="text-[10px] text-slate-400 ml-1">Henüz oy yok</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Promo + News */}
          <div className="space-y-5">
            <Link to="/etkinlikler" className="block relative overflow-hidden rounded-2xl text-white p-5 min-h-[180px] group hover:shadow-xl transition-all" data-testid="home-promo">
              <img src="/img/hero-l7e.webp" alt="" className="absolute inset-0 w-full h-full object-cover object-right transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/75 to-slate-900/20" />
              <div className="relative flex flex-col h-full min-h-[140px]">
                <h3 className="text-xl font-extrabold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Yeni bir rota keşfet!</h3>
                <p className="text-slate-300 text-sm mb-4 max-w-[70%]">L6e ve L7e araçlarla özgürce yola çıkın.</p>
                <span className="mt-auto self-start inline-flex items-center gap-1.5 bg-emerald-500 group-hover:bg-emerald-400 text-emerald-950 text-sm font-semibold px-4 py-2 rounded-full transition-colors">
                  Etkinlikleri Gör <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <SectionHeader icon={Newspaper} title="Son Haberler" to="/haberler" />
              {news.length === 0 ? (
                <EmptyState icon={Newspaper} text="Haber yok" small />
              ) : (
                <div className="space-y-3">
                  {news.slice(0, 3).map(n => (
                    <Link key={n._id} to="/haberler" data-testid={`home-news-${n._id}`}
                      className="flex gap-3 group">
                      <div className="w-14 h-14 shrink-0 rounded-lg bg-emerald-50 flex items-center justify-center overflow-hidden">
                        {n.image_url ? (
                          <img src={n.image_url.startsWith('http') ? n.image_url : `${API}/files/${n.image_url}`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Newspaper className="w-6 h-6 text-emerald-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800 line-clamp-2 group-hover:text-emerald-600">{n.title}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ROW 2.5: Garage Showcase */}
        {showcase.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mb-8" data-testid="home-garage-showcase">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Car className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-extrabold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Topluluktan Garajlar</h2>
                  <p className="text-xs text-slate-500">Üyelerin kendi araçlarından kareler</p>
                </div>
              </div>
              <Link to="/kullanicilar" className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
                Tümü <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {showcase.map(v => {
                const cover = v.cover || v.photo_path || (v.photos && v.photos[0]);
                const coverUrl = cover ? (cover.startsWith('http') ? cover : `${API}/files/${cover}`) : '';
                const avatar = v.owner?.avatar_url
                  ? (v.owner.avatar_url.startsWith('http') ? v.owner.avatar_url : `${API}/files/${v.owner.avatar_url}`)
                  : '';
                return (
                  <Link
                    key={v._id}
                    to={`/profil/${v.owner?.user_id || v.user_id}`}
                    data-testid={`showcase-vehicle-${v._id}`}
                    className="group rounded-xl overflow-hidden border border-slate-100 bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  >
                    <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
                      {coverUrl ? (
                        <img src={coverUrl} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <MicroCar color={v.category === 'L7e' ? 'cyan' : 'emerald'} className="w-20 h-20" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${v.category === 'L7e' ? 'bg-cyan-500 text-white' : 'bg-emerald-500 text-white'}`}>
                          {v.category}
                        </span>
                      </div>
                      {v.photo_count > 1 && (
                        <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                          +{v.photo_count - 1}
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 text-white">
                        <div className="text-sm font-bold truncate">{v.brand} {v.model}</div>
                        {v.custom_name && (
                          <div className="text-[10px] text-emerald-200 truncate">"{v.custom_name}"</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2.5">
                      <Avatar className="w-7 h-7 shrink-0">
                        <AvatarImage src={avatar} />
                        <AvatarFallback className="bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                          {initials(v.owner?.display_name || v.owner?.username || 'Ü')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-slate-800 truncate">{v.owner?.display_name || v.owner?.username || 'Üye'}</div>
                        <div className="text-[10px] text-slate-400">{v.year} · {timeAgo(v.created_at)}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* ROW 3: Events, Users, Badges */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Events */}
          {/* Events */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <SectionHeader icon={Calendar} title="Son Etkinlikler" to="/etkinlikler" />
            {recentEvents.length === 0 ? (
              <EmptyState icon={Calendar} text="Yaklaşan etkinlik yok" />
            ) : (
              <div className="space-y-3">
                {recentEvents.map(ev => {
                  const dt = monthShort(ev.date);
                  return (
                    <Link key={ev._id} to="/etkinlikler" data-testid={`home-event-${ev._id}`}
                      className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition">
                      <div className="w-14 h-14 shrink-0 rounded-xl bg-emerald-50 border border-emerald-100 flex flex-col items-center justify-center">
                        <span className="text-lg font-extrabold text-emerald-700 leading-none">{dt.d}</span>
                        <span className="text-[10px] text-emerald-600 uppercase mt-0.5">{dt.m}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate">{ev.title}</div>
                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{ev.location}</span>
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{ev.participant_count || 0} katılımcı</div>
                      </div>
                      <span className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                        Katıl
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Users */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <SectionHeader icon={UsersIcon} title="Son Üyeler" to="/kullanicilar" />
            {users.length === 0 ? (
              <EmptyState icon={UsersIcon} text="Henüz kullanıcı yok" />
            ) : (
              <div className="space-y-2">
                {users.map(u => (
                  <Link key={u._id} to={`/profil/${u._id}`} data-testid={`home-user-${u._id}`}
                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition">
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarImage src={u.avatar_url ? (u.avatar_url.startsWith('http') ? u.avatar_url : `${API}/files/${u.avatar_url}`) : ''} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-bold">{initials(u.display_name || u.username)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800 truncate">{u.display_name || u.username}</span>
                        {(u.badge_keys || []).slice(0, 1).map(bk => (
                          <span key={bk} className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">
                            {bk === 'l6e_uzman' ? 'L6e Uzmanı' : bk === 'l7e_uzman' ? 'L7e Uzmanı' : bk === 'aktif_uye' ? 'Aktif Üye' : bk}
                          </span>
                        ))}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {u.topic_count || 0} konu · {u.reply_count || 0} yanıt
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Badges */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <SectionHeader icon={Trophy} title="Rozetler" to="/rozetler" />
            {badges.length === 0 ? (
              <EmptyState icon={Award} text="Rozet yok" />
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {badges.map((b, idx) => (
                  <div key={b._id} className="flex flex-col items-center text-center group" data-testid={`home-badge-${b.key}`}>
                    <div className="relative mb-1.5">
                      <HexBadge index={idx} />
                    </div>
                    <span className="text-[11px] font-semibold text-slate-700 line-clamp-1">{b.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <AdSlot placement="home_bottom" className="mt-8" />
      </div>
    </div>
  );
}

function HexBadge({ index }) {
  const palettes = [
    ['#10b981', '#059669'], // emerald
    ['#ef4444', '#dc2626'], // red
    ['#f59e0b', '#d97706'], // amber
    ['#eab308', '#ca8a04'], // yellow
    ['#8b5cf6', '#7c3aed'], // violet
    ['#3b82f6', '#2563eb'], // blue
    ['#06b6d4', '#0891b2'], // cyan
  ];
  const [c1, c2] = palettes[index % palettes.length];
  const icons = [Zap, FileText, MessageSquare, Trophy, Activity, Calendar, Car];
  const Icon = icons[index % icons.length];
  return (
    <div className="w-14 h-14 relative group-hover:-translate-y-0.5 transition-transform">
      <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-sm">
        <defs>
          <linearGradient id={`gg${index}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c1} />
            <stop offset="100%" stopColor={c2} />
          </linearGradient>
        </defs>
        <path d="M50 2 L92 25 L92 75 L50 98 L8 75 L8 25 Z" fill={`url(#gg${index})`} stroke="white" strokeWidth="2" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text, cta, small }) {
  return (
    <div className={`text-center ${small ? 'py-4' : 'py-8'}`}>
      <Icon className="w-10 h-10 text-slate-200 mx-auto mb-2" />
      <p className="text-sm text-slate-500">{text}</p>
      {cta && (
        <Link to={cta.to} className="inline-block mt-2 text-emerald-600 text-sm font-semibold hover:underline">
          {cta.label}
        </Link>
      )}
    </div>
  );
}
